import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Icon } from "@astrum/ui";
import "./mention-autocomplete.css";

/**
 * Naming somebody without having to spell them right.
 *
 * The point is not convenience. What this inserts is a reference to a user id,
 * so the message records *who* was named rather than a string that has to be
 * matched back to a person later. Typing a name by hand cannot do that, which
 * is why the picker exists rather than a hint telling people to type carefully.
 */

export interface MentionCandidate {
  id: string;
  displayName: string;
  jobTitle?: string | null;
  avatarUrl?: string | null;
  /**
   * What goes after the `@` when this is picked, if not the display name —
   * `channel`, `here`, or a group's handle. These are not people, so they
   * stay plain text rather than being resolved to an id at send.
   */
  insert?: string;
}

export interface MentionAutocompleteProps {
  /** The whole composer text, and where the caret is inside it. */
  value: string;
  caret: number;
  people: MentionCandidate[];
  /** Called with the new whole text and where the caret should land. */
  onPick: (nextValue: string, nextCaret: number) => void;
  onOpenChange?: (open: boolean) => void;
}

/**
 * The `@word` being typed at the caret, if there is one.
 *
 * Only matches at a word boundary, so an email address does not open a person
 * picker halfway through being typed. Stops at whitespace: a mention is one
 * token, and a query that grew across a space would match nothing anyway.
 */
export function activeMentionQuery(value: string, caret: number): { query: string; start: number } | null {
  const before = value.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at === -1) return null;

  // Preceded by nothing or by whitespace — "foo@bar" is an address, not a
  // mention.
  const prev = at === 0 ? "" : before[at - 1]!;
  if (prev && !/\s/.test(prev)) return null;

  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  // The completed wire form contains brackets; if they are present this is
  // already a mention rather than one being typed.
  if (/[[\]()]/.test(query)) return null;

  return { query, start: at };
}

export function MentionAutocomplete({ value, caret, people, onPick, onOpenChange }: MentionAutocompleteProps) {
  const [index, setIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const active = activeMentionQuery(value, caret);

  const matches = useMemo(() => {
    if (!active) return [];
    const query = active.query.toLowerCase();
    const matchesQuery = (person: MentionCandidate) =>
      person.displayName.toLowerCase().includes(query) || (person.insert?.toLowerCase().startsWith(query) ?? false);
    return people
      .filter(matchesQuery)
      // Names that start with what was typed first: typing "li" should offer
      // "Lisa" before "Charlie".
      .sort((a, b) => {
        const aStarts = a.displayName.toLowerCase().startsWith(query) ? 0 : 1;
        const bStarts = b.displayName.toLowerCase().startsWith(query) ? 0 : 1;
        return aStarts - bStarts || a.displayName.localeCompare(b.displayName);
      })
      .slice(0, 6);
  }, [active, people]);

  const open = active !== null && matches.length > 0;

  useEffect(() => onOpenChange?.(open), [open, onOpenChange]);
  // Back to the top whenever the query changes, so the highlight is never left
  // on a row that has scrolled out of the new results.
  useEffect(() => setIndex(0), [active?.query]);

  const choose = (person: MentionCandidate) => {
    if (!active) return;
    // The readable name, not the wire form: a reference id in the middle of the
    // sentence is not something to type against. The composer converts `@Name`
    // to `@[Name](id)` at send, against these same members.
    const inserted = `@${person.insert ?? person.displayName} `;
    const next = value.slice(0, active.start) + inserted + value.slice(caret);
    onPick(next, active.start + inserted.length);
  };

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIndex((prev) => (prev + 1) % matches.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setIndex((prev) => (prev - 1 + matches.length) % matches.length);
      } else if (event.key === "Enter" || event.key === "Tab") {
        // Enter picks rather than sends while this is open — the message is
        // not finished, and sending "@div" helps nobody.
        event.preventDefault();
        const picked = matches[index];
        if (picked) choose(picked);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setIndex(0);
        onOpenChange?.(false);
      }
    };

    // Capture: the composer's own Enter handler must not fire first.
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  });

  if (!open) return null;

  return (
    <div className="mention-ac" role="listbox" aria-label="People" ref={listRef}>
      {matches.map((person, i) => (
        <button
          key={person.id}
          type="button"
          role="option"
          aria-selected={i === index}
          className={`mention-ac__row ${i === index ? "is-active" : ""}`}
          // Mouse down rather than click: the textarea loses focus on click,
          // which closes this before the selection is registered.
          onMouseDown={(event) => {
            event.preventDefault();
            choose(person);
          }}
          onMouseEnter={() => setIndex(i)}
        >
          {person.insert ? (
            <span className="mention-ac__icon" aria-hidden="true">
              <Icon name={person.insert === "channel" || person.insert === "here" ? "at" : "people"} size={14} />
            </span>
          ) : (
            <Avatar name={person.displayName} src={person.avatarUrl ?? undefined} size={22} />
          )}
          <span className="mention-ac__name">{person.displayName}</span>
          {person.jobTitle && <span className="mention-ac__title">{person.jobTitle}</span>}
        </button>
      ))}
    </div>
  );
}
